import { useState, useCallback, useEffect, useRef } from 'react'
import type { ColumnDefinition, DataTableItem, ResizeConfig } from '../types'

interface UseDataTableResizeReturn {
  columnWidths: Record<string, number>
  resizingColumnId: string | null
  onResizeStart: (columnId: string, startX: number, target: HTMLElement) => void
  getColumnWidth: (columnId: string) => number
}

interface ResizeState {
  columnId: string
  startX: number
  startWidth: number
  columnLeft: number
  minWidth: number
  maxWidth: number
  indicatorElement: HTMLDivElement | null
  tableContainer: HTMLElement
}

const DEFAULT_MIN_WIDTH = 100
const DEFAULT_MAX_WIDTH = 800

function parseWidth(width: string): number {
  const numericMatch = width.match(/^(\d+(?:\.\d+)?)(px|%)?$/)
  if (numericMatch) {
    return parseFloat(numericMatch[1])
  }
  return 100
}

export function useDataTableResize<T extends DataTableItem>(
  columns: ColumnDefinition<T>[],
  _visibleColumns: string[],
  config?: ResizeConfig
): UseDataTableResizeReturn {
  // Use ref to store columns so we don't need it as effect dependency
  const columnsRef = useRef(columns)
  columnsRef.current = columns

  const configRef = useRef(config)
  configRef.current = config

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    if (config?.storageKey) {
      const stored = localStorage.getItem(config.storageKey)
      if (stored) {
        try {
          return JSON.parse(stored)
        } catch {
          // Invalid JSON, use defaults
        }
      }
    }

    const defaultMin = config?.defaultMinWidth ?? DEFAULT_MIN_WIDTH
    const initialWidths: Record<string, number> = {}
    columns.forEach(col => {
      const parsed = parseWidth(col.width)
      if (col.resizable === false) {
        initialWidths[col.id] = parsed
      } else {
        const minWidth = col.minWidth ?? defaultMin
        initialWidths[col.id] = Math.max(parsed, minWidth)
      }
    })
    return initialWidths
  })

  const [resizingColumnId, setResizingColumnId] = useState<string | null>(null)
  const resizeStateRef = useRef<ResizeState | null>(null)

  const defaultMinWidth = config?.defaultMinWidth ?? DEFAULT_MIN_WIDTH

  // Single effect that sets up global mouse listeners when resizing starts
  useEffect(() => {
    if (!resizingColumnId || !resizeStateRef.current) return

    const state = resizeStateRef.current
    const { columnId, startX, startWidth, columnLeft, minWidth, maxWidth, indicatorElement } = state

    let currentWidth = startWidth

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - startX
      const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidth + diff))
      currentWidth = newWidth

      if (indicatorElement) {
        indicatorElement.style.left = `${columnLeft + newWidth}px`
      }
    }

    const handleMouseUp = () => {
      // Save the width
      setColumnWidths(prev => {
        const updated = { ...prev, [columnId]: currentWidth }
        const storageKey = configRef.current?.storageKey
        if (storageKey) {
          try {
            localStorage.setItem(storageKey, JSON.stringify(updated))
          } catch (e) {
            console.error('Failed to save column widths:', e)
          }
        }
        return updated
      })

      // Cleanup
      cleanup()
    }

    const cleanup = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''

      if (indicatorElement && indicatorElement.parentNode) {
        indicatorElement.parentNode.removeChild(indicatorElement)
      }

      resizeStateRef.current = null
      setResizingColumnId(null)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    return cleanup
  }, [resizingColumnId])

  const onResizeStart = useCallback((columnId: string, startX: number, target: HTMLElement) => {
    // If already resizing, ignore
    if (resizeStateRef.current) return

    // Find the header cell by traversing up from the resize handle
    const headerCell = target.closest('[data-column-id]') as HTMLElement | null
    if (!headerCell) {
      console.error('Could not find header cell')
      return
    }

    // Find the container that is an ancestor of this header cell
    const tableContainer = headerCell.closest('[data-resize-container]') as HTMLElement | null
    if (!tableContainer) {
      console.error('Could not find table container')
      return
    }

    const headerRect = headerCell.getBoundingClientRect()
    const containerRect = tableContainer.getBoundingClientRect()

    // Calculate column position relative to the container
    const columnLeft = headerRect.left - containerRect.left

    // Use actual rendered width from the DOM
    const startWidth = headerRect.width

    // Get constraints from columns ref
    const column = columnsRef.current.find(col => col.id === columnId)
    const minWidth = column?.minWidth ?? (configRef.current?.defaultMinWidth ?? DEFAULT_MIN_WIDTH)
    const maxWidth = column?.maxWidth ?? (configRef.current?.defaultMaxWidth ?? DEFAULT_MAX_WIDTH)

    // Create indicator element
    const indicator = document.createElement('div')
    indicator.style.cssText = `
      position: absolute;
      top: 0;
      bottom: 0;
      width: 2px;
      background-color: rgb(59, 130, 246);
      pointer-events: none;
      z-index: 100;
      left: ${columnLeft + startWidth}px;
    `
    tableContainer.appendChild(indicator)

    resizeStateRef.current = {
      columnId,
      startX,
      startWidth,
      columnLeft,
      minWidth,
      maxWidth,
      indicatorElement: indicator,
      tableContainer
    }

    setResizingColumnId(columnId)
  }, [])

  const getColumnWidth = useCallback((columnId: string): number => {
    const column = columnsRef.current.find(col => col.id === columnId)
    // Non-resizable columns always use the definition width
    if (column?.resizable === false) {
      return parseWidth(column.width)
    }
    const width = columnWidths[columnId]
    if (width !== undefined) {
      return width
    }
    const parsed = parseWidth(column?.width ?? '200px')
    return Math.max(parsed, defaultMinWidth)
  }, [columnWidths, defaultMinWidth])

  return {
    columnWidths,
    resizingColumnId,
    onResizeStart,
    getColumnWidth
  }
}
